import { normalizeError } from 'core/common/errors';
import { SpanAttributes, SpanName } from 'core/common/observability/entities';
import { ATTRS_ANALYTICS_EVENT_NAME } from 'core/common/observability/entities/SemanticAttributes';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { SnapchatEventNames } from '../../entities';
import { SnapchatService } from '../../interfaces/SnapchatService';
import { SnapchatServiceDecorator } from './SnapchatServiceDecorator';

export class SnapchatServiceWithMonitoring extends SnapchatServiceDecorator {
  constructor(
    snapchatService: SnapchatService,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly errorMonitoringService: ErrorMonitoring,
    private readonly receiveAdditionalObservabilityAttributes?: () => SpanAttributes,
  ) {
    super(snapchatService);
  }

  track(eventName: SnapchatEventNames, params?: Record<string, unknown>) {
    if (!super.isSnaptrRequired()) {
      try {
        return super.track(eventName, params);
      } catch {
        this.errorMonitoringService.captureError('Snapchat track failed', {
          tags: {
            error_message: 'Snapchat track failed',
            code: 'snapchat_track_failed_when_not_required',
          },
        });
        return;
      }
    }

    if (super.isSnaptrRequired() && !super.isSnaptrAvailable()) {
      try {
        return super.track(eventName, params);
      } catch {
        this.errorMonitoringService.captureError('Snapchat track failed', {
          tags: {
            error_message: 'Snapchat track failed',
            code: 'snapchat_track_failed_when_required_not_available',
          },
        });
        return;
      }
    }

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_SNAPCHAT);
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: eventName,
      ...(this.receiveAdditionalObservabilityAttributes
        ? this.receiveAdditionalObservabilityAttributes()
        : {}),
    };
    span.setAttributes(spanAttributes);

    try {
      const result = super.track(eventName, params);
      span.succeed();
      return result;
    } catch (err) {
      const error = normalizeError(err);
      span.fail(error);
      return;
    }
  }
}
